import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  ApiError,
  getGetRespondentQuestionnaireQueryKey,
  submitRespondentAnswers,
  type SubmitAnswersInput,
  type SubmitAnswersResponse,
} from "@wellcheck/api-client";

/**
 * useSubmitAnswers — 回答提出 (POST /api/respondent-answers, ADR-0024)。
 *
 * POST は orval.config.ts の方針 (query.useQuery のみ生成、mutation は低レベル関数を
 * 呼び出し側で useMutation にラップ) に従い、生成済みの submitRespondentAnswers を直接呼ぶ。
 *
 * 409 (二重提出 / 提出不可) は backend 側で 2 ケースとも huma.Error409Conflict + detail 文字列
 * のみで返り、機械可読な discriminator が無い (ADR-0024 に discriminator の記載なし)。
 * frontend 側では 409 を一律「提出できない状態」として扱い、呼び出し側 (SurveyForm) で
 * 提出済み画面に倒す。detail 文字列での分岐はサーバ側が契約として保証していないため行わない。
 *
 * 成功時は respondent-questionnaire の status が completed に変わるため、
 * 該当 query を invalidate して再訪時の描画を最新化する。
 */
export function useSubmitAnswers() {
  const queryClient = useQueryClient();
  return useMutation<SubmitAnswersResponse, ApiError, SubmitAnswersInput>({
    mutationFn: async (input: SubmitAnswersInput) => {
      const response = await submitRespondentAnswers(input);
      if (response.status !== 200) {
        throw new ApiError(response.status, response.data, response.headers);
      }
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: getGetRespondentQuestionnaireQueryKey(),
      });
    },
  });
}
